import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, onSnapshot, query, orderBy, where, Timestamp, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { GAME_STATES, GAME_CONFIG } from '../config/gameConfig';
import { parseCreatedAt } from '../utils/timeUtils';

// 스타일 정의
const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  pointer-events: none;
  z-index: 9999;
`;

const BannerBackground = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    height: 30px;
  }
`;

const SlideMessage = styled(motion.div)`
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  padding: 0 20px;
  position: absolute;
  left: 0;
  top: 0;
  transform: none;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// 로컬스토리지 처리 함수 (새로고침 시 초기화)
const STORAGE_KEY = 'shownRouletteIds';
const MAX_STORED_IDS = 1000; // 최대 저장할 ID 수 제한

function getStoredShownIds() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const ids = JSON.parse(stored);
      // 최대 개수 제한
      return new Set(ids.slice(-MAX_STORED_IDS));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

function saveShownIds(ids) {
  try {
    // 배열로 변환하고 최대 개수 제한
    const idArray = Array.from(ids).slice(-MAX_STORED_IDS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(idArray));
      } catch (error) {
      //console.warn('로컬스토리지 저장 실패:', error);
    }
}

// 전역 상태로 실시간 리스너 관리 (싱글톤 패턴)
let globalListener = null;
let listenerSubscribers = new Set();
let isListenerActive = false;

const RouletteBanner = () => {
  const [messageQueue, setMessageQueue] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const shownIds = useRef(getStoredShownIds());
  const retryTimeoutRef = useRef(null);
  
  // 모바일/웹 구분
  const isMobile = window.innerWidth <= 768;
  const animationDuration = isMobile ? 10 : 20; // 모바일 10초, 웹 20초
  const timerDuration = isMobile ? 10000 : 20000; // 모바일 10초, 웹 20초

  // 전역 리스너 초기화
  const initializeGlobalListener = () => {
    if (isListenerActive) return;

    try {
      // 5분 전 시간 계산 (서버 시간 기준)
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      const fiveMinutesAgoTimestamp = Timestamp.fromDate(fiveMinutesAgo);
      
      const q = query(
        collection(db, 'gameEvent', GAME_CONFIG.EVENT_ID, GAME_STATES.ROULETTE_RESULT),
        where('timestamp', '>=', fiveMinutesAgoTimestamp),
        orderBy('timestamp', 'asc')
      );

      globalListener = onSnapshot(q, (snapshot) => {
        const newMsgs = [];
        const docs = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const timestamp = data.timestamp?.toDate ? data.timestamp.toDate().getTime() : new Date(data.timestamp).getTime();
          
          docs.push({
            id: doc.id,
            data,
            timestamp,
          });
        });

        docs.forEach((doc) => {
          if (!shownIds.current.has(doc.id)) {
            newMsgs.push({
              id: doc.id,
              nickname: doc.data.nickname,
              prizeName: doc.data.prizeName,
            });
          }
        });
        
        // 모든 구독자에게 메시지 전달
        listenerSubscribers.forEach(callback => {
          if (newMsgs.length > 0) {
            callback(newMsgs);
          }
        });
      }, (error) => {
        //console.error('실시간 리스너 오류:', error);
        setConnectionError(true);
        isListenerActive = false;
        
        // 재연결 시도 (5초 후)
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          initializeGlobalListener();
        }, 5000);
      });

      isListenerActive = true;
      setConnectionError(false);
    } catch (error) {
      //console.error('리스너 초기화 실패:', error);
      setConnectionError(true);
    }
  };

  // 폴링 방식으로 대체 (실시간 리스너 실패 시)
  const startPolling = () => {
    const pollInterval = setInterval(async () => {
      // 실시간 리스너가 활성화되어 있으면 폴링 건너뛰기
      if (isListenerActive) {
        return;
      }
      
      try {
        // 5분 전 시간 계산 (서버 시간 기준)
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
        const fiveMinutesAgoTimestamp = Timestamp.fromDate(fiveMinutesAgo);
        
        const q = query(
          collection(db, 'gameEvent', GAME_CONFIG.EVENT_ID, GAME_STATES.ROULETTE_RESULT),
          where('timestamp', '>=', fiveMinutesAgoTimestamp),
          orderBy('timestamp', 'asc')
        );

        const snapshot = await getDocs(q);
        const newMsgs = [];

        snapshot.forEach((doc) => {
          if (!shownIds.current.has(doc.id)) {
            const data = doc.data();
            newMsgs.push({
              id: doc.id,
              nickname: data.nickname,
              prizeName: data.prizeName,
            });
          }
        });

        if (newMsgs.length > 0) {
          setMessageQueue((prev) => [...prev, ...newMsgs]);
        }
      } catch (error) {
        //console.error('폴링 오류:', error);
      }
    }, 10000); // 10초마다 폴링

    return () => clearInterval(pollInterval);
  };

  useEffect(() => {
    // 구독자 등록
    const messageHandler = (newMsgs) => {
      setMessageQueue((prev) => [...prev, ...newMsgs]);
    };
    
    listenerSubscribers.add(messageHandler);

    // 전역 리스너 초기화 시도
    initializeGlobalListener();

    // 폴링 시작 (백업 방식) - 실시간 리스너 실패 시에만 사용
    const pollingCleanup = startPolling();

    return () => {
      // 구독자 제거
      listenerSubscribers.delete(messageHandler);
      
      // 폴링 정리
      pollingCleanup();
      
      // 재연결 타이머 정리
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!currentMessage && messageQueue.length > 0) {
      const next = messageQueue[0];
      setCurrentMessage(next);
    }
  }, [messageQueue, currentMessage]);
  
  useEffect(() => {
    if (!currentMessage) return;
    
    const timeout = setTimeout(() => {
      // 메시지가 완전히 지나간 후 로컬스토리지에 저장
      shownIds.current.add(currentMessage.id);
      saveShownIds(shownIds.current);
     
      setMessageQueue((prev) => prev.slice(1));
      setCurrentMessage(null);
    }, timerDuration);

    return () => clearTimeout(timeout);
  }, [currentMessage]);

  return (
    <Container>
      <BannerBackground>
        <AnimatePresence>
          {currentMessage && (
            <SlideMessage
              key={currentMessage.id}
              initial={{ x: '100vw' }}
              animate={{ x: '-100vw' }}
              exit={{ opacity: 0 }}
              transition={{ duration: animationDuration, ease: 'linear' }}
            >
              🎉 <strong>{currentMessage.nickname}</strong> 님,{' '}
              <strong>{currentMessage.prizeName}</strong> 당첨되셨습니다!
            </SlideMessage>
          )}
        </AnimatePresence>
      </BannerBackground>
    </Container>
  );
};

export default RouletteBanner;
